import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { Dialog } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { Button, DialogView } from '../../../components'
import { fetchStores } from '../../../services/storeService'
import { mapConfigError, parsePercent } from './packingAuditConfig'

const T = 'packing_config.audit'
const PRESETS = [0, 50, 100]
const inputClasses = 'shadow-sm block w-full sm:text-sm rounded-md border border-gray-300 px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500'

export const StoreExceptionModal = ({ open, setOpen, warehouseId, initialStore = null, initialValue, excludedStoreIds = [], warehouseValue, onSubmit }) => {
  const { t } = useTranslation()
  const editing = initialStore !== null && initialValue !== undefined
  const latestSearch = useRef(0)
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchedFor, setSearchedFor] = useState(null)
  const [searchFailed, setSearchFailed] = useState(false)
  const [store, setStore] = useState(initialStore)
  const [draft, setDraft] = useState(initialValue !== undefined ? String(initialValue) : '')
  const [touched, setTouched] = useState(false)
  const [serverError, setServerError] = useState(null)
  const [saving, setSaving] = useState(false)

  const searchStores = useMemo(
    () =>
      debounce(async (text, excluded) => {
        const name = text.trim()
        // Every query supersedes the one in flight, including one too short to search.
        const seq = ++latestSearch.current
        if (name.length < 2) {
          setOptions([])
          setSearchedFor(null)
          setSearchFailed(false)
          setSearching(false)
          return
        }
        // Marks the window from the debounced call's start to its sequenced response, so the
        // dialog can show a "searching" line instead of a stale results/no-results state.
        setSearching(true)
        try {
          const res = await fetchStores({ warehouse_id: warehouseId, name, per_page: 20 })
          if (seq !== latestSearch.current) return
          setOptions((res.stores || []).filter(s => !excluded.includes(s.id)).map(s => ({ id: s.id, name: s.name })))
          setSearchFailed(false)
        } catch {
          if (seq !== latestSearch.current) return
          setOptions([])
          setSearchFailed(true)
        }
        if (seq !== latestSearch.current) return
        setSearching(false)
        setSearchedFor(name)
      }, 300),
    [warehouseId],
  )
  useEffect(
    () => () => {
      searchStores.cancel()
      latestSearch.current += 1
    },
    [searchStores],
  )

  // The card keeps this modal mounted while closed, so every open/close drops a pending or in-flight search.
  useEffect(() => {
    searchStores.cancel()
    latestSearch.current += 1
    if (!open) return
    setQuery('')
    setOptions([])
    setSearching(false)
    setSearchedFor(null)
    setSearchFailed(false)
    setStore(initialStore)
    setDraft(initialValue !== undefined ? String(initialValue) : '')
    setTouched(false)
    setServerError(null)
  }, [open, initialStore, initialValue, searchStores])

  // `DialogView`'s `initialFocus` ref is never attached, so Headless UI's FocusTrap would focus the
  // X button. This ref focuses the field when it mounts: a commit after `open` flips (an effect on
  // `open` misses it), inside a portal node that joins the document later in that commit (an
  // immediate focus or `autoFocus` is a no-op). The microtask runs after that and before
  // FocusTrap's initial-focus microtask, which then finds focus inside and leaves it.
  const focusOnMount = useCallback(node => {
    if (node) queueMicrotask(() => node.focus())
  }, [])

  // `DialogView` only calls `onClose` (so Escape reaches it) when `closeOnBackdrop` is set, and
  // turning that on would also close the modal on a backdrop click, which is not asked for here —
  // so Escape is handled independently, only while the modal isn't mid-save.
  useEffect(() => {
    if (!open) return
    const onKeyDown = e => {
      if (e.key === 'Escape' && !saving) setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, saving, setOpen])

  const onQueryChange = e => {
    setQuery(e.target.value)
    searchStores(e.target.value, excludedStoreIds)
  }

  const parsed = parsePercent(draft)
  // In edit mode an unchanged value would only send a no-op PUT that stamps the current user as the
  // last editor, so Guardar (and Enter) wait for a change, as the warehouse field does.
  const canSave = Boolean(store) && parsed.value !== undefined && !saving && (!editing || parsed.value !== initialValue)

  const submit = async () => {
    setSaving(true)
    setServerError(null)
    try {
      await onSubmit(store, parsed.value)
      setOpen(false)
    } catch (error) {
      setServerError(t(mapConfigError(error)))
    } finally {
      setSaving(false)
    }
  }

  const onFormSubmit = e => {
    e.preventDefault()
    // After a save the form stays mounted through the leave transition, with Guardar enabled again
    // once `saving` drops, so a double-click or a second Enter there must not send the PUT twice.
    if (!open || !canSave) return
    submit()
  }

  return (
    <DialogView open={open} setOpen={setOpen}>
      {/* DialogView appends className without a space, pins sm:max-w-5xl and its root does not scroll, so the width
          and the scroll live on this wrapper (inline style: Tailwind 2 classic has no arbitrary values). */}
      <form onSubmit={onFormSubmit} className="space-y-4 overflow-y-auto" style={{ width: 'min(32rem, calc(100vw - 4rem))', maxHeight: 'calc(100vh - 6rem)' }}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
              {t(editing ? `${T}.modal.edit_title` : `${T}.modal.add_title`)}
            </Dialog.Title>
            <p className="text-sm text-gray-500">{t(`${T}.modal.subtitle`, { value: warehouseValue })}</p>
          </div>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-500"
            onClick={() => setOpen(false)}
            disabled={saving}
            aria-label={t(`${T}.modal.close`)}
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {store ? (
          <div className="flex items-center justify-between rounded-md bg-gray-50 border border-gray-200 px-3 py-2">
            <span className="text-sm font-medium text-gray-900">{store.name}</span>
            {!editing && (
              <button
                type="button"
                className="text-xs text-indigo-600 hover:underline disabled:opacity-50"
                onClick={() => {
                  setStore(null)
                  setServerError(null)
                }}
                disabled={saving}
              >
                {t(`${T}.modal.change_store`)}
              </button>
            )}
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="exception-store-search">
              {t(`${T}.modal.store_label`)}
            </label>
            <input
              id="exception-store-search"
              ref={focusOnMount}
              type="text"
              className={inputClasses}
              placeholder={t(`${T}.modal.store_placeholder`)}
              value={query}
              onChange={onQueryChange}
              onKeyDown={e => {
                // This field never submits the form: Enter here just types, it doesn't pick a
                // (possibly stale) top result.
                if (e.key === 'Enter') e.preventDefault()
              }}
              autoComplete="off"
            />
            {searching ? (
              <p className="mt-1 text-xs text-gray-500">{t(`${T}.modal.searching`)}</p>
            ) : options.length > 0 ? (
              <ul className="mt-1 border border-gray-200 rounded-md divide-y divide-gray-100 max-h-56 overflow-auto">
                {options.map(option => (
                  <li key={option.id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50"
                      onClick={() => {
                        setStore(option)
                        setServerError(null)
                      }}
                    >
                      {option.name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              searchedFor !== null &&
              searchedFor === query.trim() && (
                <p className={`mt-1 text-xs ${searchFailed ? 'text-red-600' : 'text-gray-500'}`}>
                  {t(searchFailed ? `${T}.modal.search_error` : `${T}.modal.no_results`)}
                </p>
              )
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="exception-percentage">
            {t(`${T}.modal.value_label`)}
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative w-28">
              <input
                id="exception-percentage"
                ref={editing ? focusOnMount : undefined}
                type="text"
                inputMode="numeric"
                // Greyed while it is read-only mid-save, a busy cue like the dimmed chips.
                className={`${inputClasses} pr-8 text-right ${saving ? 'bg-gray-50 text-gray-500' : ''}`}
                value={draft}
                onChange={e => {
                  setDraft(e.target.value)
                  setTouched(true)
                  setServerError(null)
                }}
                // The PUT in flight already carries the value, so a change here or on a chip mid-save
                // would be silently dropped. Read-only rather than disabled: disabling blurs the field,
                // which would leave an Enter submitter on the page body after a refused save.
                readOnly={saving}
              />
              <span className="absolute inset-y-0 right-3 flex items-center text-sm text-gray-500">%</span>
            </div>
            {PRESETS.map(preset => (
              <button
                key={preset}
                type="button"
                // The parsed value, not the raw text: "050" is saved as 50, so it lights the 50% chip.
                aria-pressed={parsed.value === preset}
                onClick={() => {
                  setDraft(String(preset))
                  setTouched(true)
                  setServerError(null)
                }}
                disabled={saving}
                className={`px-2.5 py-1 rounded-full text-xs border disabled:opacity-50 ${
                  parsed.value === preset ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {preset === 0 ? t(`${T}.modal.preset_exclude`) : `${preset}%`}
              </button>
            ))}
          </div>
          {(touched || draft !== '') && parsed.error && <p className="mt-1 text-xs text-red-600">{t(`${T}.errors.${parsed.error}`)}</p>}
          {parsed.value === 0 && <p className="mt-1 text-xs text-gray-600">{t(`${T}.modal.excluded_hint`)}</p>}
        </div>

        {serverError && (
          <div role="alert" className="rounded-md bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2">
            {serverError}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="secondary" onClick={() => setOpen(false)} disabled={saving}>
            {t(`${T}.modal.cancel`)}
          </Button>
          <Button type="primary" buttonType="submit" disabled={!canSave} loading={saving} loading_label={t(`${T}.saving`)}>
            {t(`${T}.modal.save`)}
          </Button>
        </div>
      </form>
    </DialogView>
  )
}
